import { createRouter, createWebHistory } from 'vue-router'
import { auth, onAuthStateChanged, getUserRole } from '@/api' // Import the utility function

// Import Views
import Home from '../views/Home.vue'
import About from '../views/About.vue'
import Courses from '../views/Courses.vue'
import Notices from '../views/Notices.vue'
import AccessInfo from '../views/AccessInfo.vue'
import Contact from '../views/Contact.vue'
import Admin from '../views/Admin.vue' // Import Admin view
import UserAuth from '../views/UserAuth.vue' // Import UserAuth view
import UserProfile from '../views/UserProfile.vue' // Import UserProfile view
import ResetPassword from '../views/ResetPassword.vue' // Import ResetPassword view
import NotFound from '../views/NotFound.vue'

// Function to get current user authentication state
const getCurrentUser = () => {
  return new Promise((resolve, reject) => {
    let unsubscribe;
    unsubscribe = onAuthStateChanged(auth, user => {
      if (unsubscribe) {
        unsubscribe();
      }
      resolve(user);
    }, reject);
  });
};

const routes = [
  {
    path: '/',
    name: 'Home',
    component: Home,
  },
  {
    path: '/about',
    name: 'About',
    component: About,
  },
  {
    path: '/courses',
    name: 'Courses',
    component: Courses,
  },
  {
    path: '/notices',
    name: 'Notices',
    component: Notices,
  },
  {
    path: '/access',
    name: 'Access',
    component: AccessInfo,
  },
  {
    path: '/contact',
    name: 'Contact',
    component: Contact,
  },
  {
    path: '/admin',
    name: 'Admin',
    component: Admin,
    meta: { requiresAuth: true, requiresAdmin: true } // Add meta fields for auth requirements
  },
  {
    path: '/auth',
    name: 'UserAuth',
    component: UserAuth,
    meta: { requiresGuest: true } // Redirect logged-in users from /auth
  },
  {
    path: '/profile',
    name: 'UserProfile',
    component: UserProfile,
    meta: { requiresAuth: true } // Add meta field for auth requirement
  },
  {
    path: '/reset-password',
    name: 'ResetPassword',
    component: ResetPassword,
    meta: { requiresGuest: true } // Only allow guests to access reset password page
  },
  // Catch-all 404 route
  {
    path: '/:catchAll(.*)',
    name: 'NotFound',
    component: NotFound,
  },
]

const router = createRouter({
  history: createWebHistory(process.env.BASE_URL),
  scrollBehavior(/* to, from, savedPosition */) {
    // always scroll to top
    return { top: 0 }
  },
  routes
})

// --- Navigation Guards ---
router.beforeEach(async (to, from, next) => {
  const currentUser = await getCurrentUser();
  const userRole = currentUser ? await getUserRole(currentUser) : null;

  const requiresAuth = to.matched.some(record => record.meta.requiresAuth);
  const requiresAdmin = to.matched.some(record => record.meta.requiresAdmin);
  const requiresGuest = to.matched.some(record => record.meta.requiresGuest);

  console.log(`Navigating to: ${to.path}`);
  console.log(`Current User: ${currentUser ? currentUser.email : 'None'}`);
  console.log(`User Role: ${userRole}`);
  console.log(`Requires Auth: ${requiresAuth}`);
  console.log(`Requires Admin: ${requiresAdmin}`);
  console.log(`Requires Guest: ${requiresGuest}`);

  if (requiresAuth && !currentUser) {
    // Needs login but is not logged in
    console.log('Redirecting to /auth because requiresAuth is true and no currentUser');
    next({ name: 'UserAuth', query: { redirect: to.fullPath } }); // Redirect to login, saving intended destination
  } else if (requiresAdmin && userRole !== 'admin') {
    // Needs admin role but user is not admin (or not logged in)
    console.log('Redirecting to / because requiresAdmin is true but user is not admin');
    next({ name: 'Home' }); // Redirect non-admins away from admin page
  } else if (requiresGuest && currentUser) {
    // Should only be accessed by guests (like login page), but user is logged in
    console.log('Redirecting to / because requiresGuest is true and currentUser exists');
    next({ name: 'Home' }); // Redirect logged-in users away from auth page
  } else {
    // Allow navigation
    console.log('Allowing navigation');
    next();
  }
});

export default router 